// Keep in sync with org service / shared `MAX_SUBFOLDER_DEPTH`:
// packages/shared/.../copyToAnotherAccountUtils.ts
export const MAX_SUBFOLDER_DEPTH = 5;

interface FolderParentReference {
  // Matches FlatFolderTreeItem.parentId from the folder-tree API.
  parentId?: string | number | null;
}

/**
 * Max depth of the subtree rooted at `folderId`, where the root is depth 1.
 * Uses the flat folder-tree hash (parentId links) already loaded on the client.
 */
export function computeFolderSubtreeMaxDepthFromHash(
  folderId: string,
  hash: Record<string, FolderParentReference | undefined>,
): number {
  const childrenByParent = new Map<string, string[]>();
  for (const [id, folder] of Object.entries(hash)) {
    // `== undefined` also treats API `null` parentIds as top-level.
    if (folder?.parentId == undefined) continue;
    const parentId = String(folder.parentId);
    const siblings = childrenByParent.get(parentId);
    if (siblings) {
      siblings.push(id);
    } else {
      childrenByParent.set(parentId, [id]);
    }
  }

  let maxDepth = 1;
  const stack: { id: string; depth: number }[] = [{ id: folderId, depth: 1 }];
  while (stack.length > 0) {
    const current = stack.pop()!;
    maxDepth = Math.max(maxDepth, current.depth);
    for (const childId of childrenByParent.get(current.id) ?? []) {
      stack.push({ id: childId, depth: current.depth + 1 });
    }
  }
  return maxDepth;
}

/** True when Move has no valid destination (already top-level + subtree at max). */
export function isFolderMoveBlockedByDepth(input: {
  isTopLevel: boolean;
  sourceSubtreeMaxDepth: number | undefined;
  maxSubfolderDepth?: number;
}): boolean {
  if (!input.isTopLevel) return false;
  if (input.sourceSubtreeMaxDepth === undefined) return false;
  return (
    input.sourceSubtreeMaxDepth >=
    (input.maxSubfolderDepth ?? MAX_SUBFOLDER_DEPTH)
  );
}
